import React from "react";
import { Box, Typography, Paper, Stack, Divider } from "@mui/material";
import PageHeader from "../components/PageHeader";

type Entry = {
  version: string;
  date: string;
  notes: string[];
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
};

export default function Changelog() {
  const entries: Entry[] = [
    {
      version: "v0.1.23",
      date: "2026-07-23",
      notes: [
        "Surveys dashboard: users can now permanently dismiss the survey-development assistance banner, with the preference saved to their account across devices and future sessions.",
      ],
    },
    {
      version: "v0.1.22",
      date: "2026-07-23",
      notes: [
        "Surveys dashboard: added a prominent survey-development assistance banner with direct email contact for the WHO Collaborating Centre (CC) at QUB.",
        "Navigation: added a Help page with interim support guidance and a direct email link.",
      ],
    },
    {
      version: "v0.1.21",
      date: "2026-07-23",
      notes: [
        "Survey viewer: improved Firestore-backed dropdown search so matches at the start of a name or word rank above mid-word matches, while all matching choices remain available.",
      ],
    },
    {
      version: "v0.1.20",
      date: "2026-07-21",
      notes: [
        "Survey viewer: the before-you-continue reminder now also appears on pages where respondents can add another person, with wording tailored to people or organizations.",
      ],
    },
    {
      version: "v0.1.19",
      date: "2026-06-05",
      notes: [
        "Survey editor: admins can now assign any active Firestore list to dropdown questions, with non-owned admin-only list choices labelled by owner email.",
      ],
    },
    {
      version: "v0.1.18",
      date: "2026-06-05",
      notes: [
        "Survey manager: added a progress-bar setting so managers can switch the public survey viewer from the default SurveyJS progress bar to a percentage progress bar.",
      ],
    },
    {
      version: "v0.1.17",
      date: "2026-05-06",
      notes: [
        "Analytics: Firestore-backed dropdown choices are now hydrated before rendering results, so organization answers stored from linked lists display correctly.",
      ],
    },
    {
      version: "v0.1.16",
      date: "2026-04-15",
      notes: [
        "Surveys list: added an optional “Populate Test Data” button that appears for signed-in users when enabled by admin settings.",
        "Test data population: copies configured template surveys and lists into the current user account, does not copy survey responses, and remaps dropdown list bindings to the user’s cloned list IDs.",
        "Admin tools: added a new “Manage Test Data” screen at /admin/test-data to toggle availability and choose which surveys/lists are available as copy templates.",
        "Admin test-data selection: survey templates now exclude deleted surveys (including legacy deleted flags), and both survey/list template pickers are restricted to records owned by the logged-in admin.",
        "Populate naming: copied surveys and lists now keep source names and auto-resolve duplicates as Name, Name (1), Name (2), etc.",
        "Deleted items: /deleted now supports both surveys and lists for restore/hard-delete workflows (including list item cleanup on hard delete).",
        "Firestore rules: added adminSettings access control and template-survey read access for configured test templates while the feature is enabled.",
      ],
    },
    {
      version: "v0.1.15",
      date: "2026-04-09",
      notes: [
        "Admin tools: added an admin-only Rules Reference page at /admin/rules with both a permissions matrix and raw Firestore rules snapshot.",
        "Admin tools: diagnostics URL is now under /admin/diagnostics/surveys and remains read-only.",
        "Admin tools: centralized navigation through /admin with direct links to users, survey master, diagnostics, and rules.",
      ],
    },
    {
      version: "v0.1.14",
      date: "2026-04-09",
      notes: [
        "Admin tools: added a new admin-only hub page at /admin linking User Manager, Survey Master, and Survey Shape Diagnostics.",
        "Admin diagnostics: moved survey-shape diagnostics to /admin/diagnostics/surveys and allowed access on deployed environments for admins (still read-only).",
        "Admin survey master: added a dedicated /admin/surveys screen grouped by published/unpublished/deleted with owner transfer dropdowns.",
        "Firestore rules: admins can now update survey ownership fields during controlled ownership transfers.",
      ],
    },
    {
      version: "v0.1.13",
      date: "2026-04-08",
      notes: [
        "Survey editor: editors can now make the first save on shared blank surveys, including older survey documents that do not yet have `ownerId` normalized.",
        "Survey editor: 'Load from Firestore List' now hides soft-deleted lists so deleted sources no longer appear in dropdown-list loading.",
      ],
    },
    {
      version: "v0.1.12",
      date: "2026-04-07",
      notes: [
        "Admin users: added an admin-only user manager screen listing Firestore user profiles, account roles, and timestamps.",
        "Admin users: admins can now promote or demote users between user and admin roles from the app.",
        "Accounts: sign-in flow now records last login time in the user profile so admins can see recent activity.",
      ],
    },
    {
      version: "v0.1.11",
      date: "2026-04-07",
      notes: [
        "Maintenance: cleaned up the login/signup password-policy implementation after the v0.1.10 release.",
      ],
    },
    {
      version: "v0.1.10",
      date: "2026-04-07",
      notes: [
        "Sign-up: upgraded password policy from a 6-character minimum to a 12-character rule requiring uppercase, lowercase, and number.",
        "Sign-up: simplified password guidance so users only see a compact message when the entered password is still missing a required element.",
        "Sign-up: improved password-manager hints for new-account registration and kept live confirm-password matching feedback.",
      ],
    },
    {
      version: "v0.1.9",
      date: "2026-04-07",
      notes: [
        "Survey editor: editors can now save survey-definition changes without requiring owner access.",
        "Firebase config: added the missing main-project staging hosting target wiring for stakeholdernet-c95e5.",
        "Release process: deployment instructions now document the isolated staging project, the main-project staging site, and the required version bump step.",
      ],
    },
    {
      version: "v0.1.8",
      date: "2026-02-17",
      notes: [
        "Survey viewer: made the 'Add another organization' action button permanently prominent (not hover-only).",
        "Survey viewer: replaced browser confirm with an in-app modal prompt before continuing when organization add actions are present.",
        "Survey viewer: improved organization prompt detection to cover organization/organisation label variants and next/complete navigation paths.",
        "Network graph: added optional node hover tooltip (default off) with node-level metrics (in-degree, out-degree, total degree, betweenness, closeness).",
      ],
    },
    {
      version: "v0.1.5",
      date: "2026-01-27",
      notes: [
        "Network graph: ribbon-style control panel with tabbed sections replaces the crowded multi-row controls.",
        "Network graph: label sizing + collision now account for measured label widths and optional backgrounds.",
        "Network graph: layout controls update live without full simulation resets.",
        "Network graph: per-user per-network UI settings persist in Firestore with reset to defaults.",
      ],
    },
    {
      version: "v0.1.2",
      date: "2026-01-12",
      notes: [
        "Network Details: added relationship-type filter with per-type dimension selection.",
        "Network Details: relationship-type filter uses colored chips and expands wider for long labels.",
        "Network graph: clarified mode help text and tooltip guidance.",
      ],
    },
    {
      version: "v0.1.1",
      date: "2026-01-10",
      notes: [
        "Added inline list item splitting with dedupe and sorting in List Edit.",
        "Enabled inline edits for sources/nominations and improved split detection in network debug.",
        "Added a dedicated changelog page with version link from the surveys list.",
      ],
    },
    {
      version: "v0.1.0",
      date: "2025-12-18",
      notes: ["Initial release of dashboard and surveys management."],
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <PageHeader title="Build Changelog" subtitle="Recent changes and release notes" />

      <Stack spacing={2}>
        {entries.map((entry, idx) => (
          <Paper key={`${entry.version}-${idx}`} variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {entry.version}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(entry.date)}
              </Typography>
            </Box>
            <Divider sx={{ my: 1.5 }} />
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {entry.notes.map((note, noteIdx) => (
                <li key={noteIdx}>
                  <Typography variant="body2" color="text.secondary">
                    {note}
                  </Typography>
                </li>
              ))}
            </ul>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
